import React, { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { Hourglass, Info } from 'lucide-react';

import { useUser } from '../../../contexts/UserContext';
import { cn } from '../../../lib/utils';
import HoverTip from '../../common/HoverTip';

// In Process is the floor (Nic 2026-10-09). Nobody moves a claim back to
// Intake or Awaiting Med. Recs. by hand; its Claims Manager marks it Waiting
// on documents instead (it stays In Process with them, the kanban tile shows
// it; partners never see it). Send back to Onboarding was removed before
// launch (Nic 2026-10-22): wrong agreements are corrected from Signed SAs.

const CM_STATUSES = [
  'in process',
  'awaiting internal review',
  'internal review approved',
  'submitted to va',
];
// The server caps it too (onboardingClaimLink WAITING_NOTE_MAX).
const NOTE_MAX = 60;

const errorText = (e, fallback) => e?.response?.data?.msg || fallback;

// The look (Nic 2026-10-22): the same card language as the Claims Manager
// card above it, with a Tailwind switch.
const FIELD =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20';

const Switch = ({ on, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!on)}
    // Fixed track + explicit knob offsets: Bootstrap's button padding and
    // box model otherwise push the knob past the right edge.
    className="shrink-0 border-0 bg-transparent p-0 outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 disabled:opacity-40"
  >
    <span
      className={cn('relative block rounded-full transition-colors', on ? 'bg-amber-500' : 'bg-slate-300')}
      style={{ width: 36, height: 20 }}
    >
      <span
        className="absolute rounded-full bg-white shadow transition-[left] duration-150"
        style={{ width: 16, height: 16, top: 2, left: on ? 18 : 2 }}
      />
    </span>
  </button>
);

const ClaimHandoffCard = ({ claim, canEdit = true }) => {
  const { user, hasPermission } = useUser();
  const queryClient = useQueryClient();
  const refresh = () => {
    queryClient.invalidateQueries('claim-profile');
    queryClient.invalidateQueries(['claim-profile', claim.claimId]);
  };

  const waiting = !!claim.claimWaitingOnDocsAt;
  // What the switch shows: flips on the click, then follows the claim.
  const [shownOn, setShownOn] = useState(waiting);
  useEffect(() => setShownOn(waiting), [waiting]);
  const [note, setNote] = useState(claim.claimWaitingOnDocsNote || '');
  const [savingWait, setSavingWait] = useState(false);
  useEffect(() => setNote(claim.claimWaitingOnDocsNote || ''), [claim.claimWaitingOnDocsNote]);

  const isCmClaim = CM_STATUSES.includes(claim.claimStatus);
  const canFlag =
    canEdit &&
    hasPermission('claims:edit') &&
    (hasPermission('claims:view_all') || claim.claimAssignedToUserId === user?.userId);
  if (!isCmClaim || (!canFlag && !waiting)) return null;

  const setWaiting = async (on) => {
    if (savingWait) return;
    setShownOn(on);
    setSavingWait(true);
    try {
      await axios.put(`/claims/${claim.claimId}/profile/waiting-on-docs`, { on, note: on ? note : null });
      toast.success(on ? 'Marked as waiting on documents.' : 'No longer waiting on documents.');
      refresh();
    } catch (e) {
      setShownOn(!on);
      toast.error(errorText(e, "That didn't save."));
    } finally {
      setSavingWait(false);
    }
  };

  return (
    <Card className="mb-3">
      <Card.Body>
        <div className="flex items-start gap-3">
          <span
            className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
              waiting ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500',
            )}
          >
            <Hourglass size={16} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="text-[15px] font-bold leading-tight text-slate-800">Waiting on documents?</span>
              <HoverTip
                text={
                  <>
                    Puts an <Hourglass size={11} className="mx-0.5 inline align-[-1px]" /> on this claim&apos;s tile
                    on the Claims board, so you can see it at a glance.
                  </>
                }
              >
                <Info
                  size={14}
                  aria-label="What does this do?"
                  className="cursor-help text-slate-400 hover:text-slate-600"
                />
              </HoverTip>
            </div>
            <div className="mt-0.5 text-xs text-slate-500">Just mark it here so you can keep track.</div>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <Switch on={shownOn} label="Waiting on documents" disabled={!canFlag} onChange={setWaiting} />
          {waiting ? (
            <div className="min-w-0 max-w-md flex-1 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Since {moment(claim.claimWaitingOnDocsAt).format('MMM D')}
              {claim.claimWaitingOnDocsNote ? `: ${claim.claimWaitingOnDocsNote}` : ''}
            </div>
          ) : canFlag ? (
            <input
              className={cn(FIELD, 'max-w-md flex-1')}
              value={note}
              maxLength={NOTE_MAX}
              placeholder="What are you waiting on? (optional)"
              onChange={(e) => setNote(e.target.value)}
            />
          ) : null}
        </div>
      </Card.Body>
    </Card>
  );
};

export default ClaimHandoffCard;
